import { ContextMenu } from "@components/common/context-menu";
import { MONTH, FORMAT } from "@resources/Constants";
import moment from "moment";
import { useCallback, useEffect, useId, useRef } from "react";
import DatePicker, { ReactDatePickerCustomHeaderProps } from "react-datepicker";
import { classNames } from "@utils/Global";
import { HVIcon, HVButton } from "@repo/HVComponents";
import "react-datepicker/dist/react-datepicker.css";
import { useAppSelector } from "@configs/Redux";
import { useLang } from "@hooks/useLang";
import { capitalizeFirstLetter } from "@components/report/widget/widget-statistics/helper";
import { CalendarProps } from "./Interfaces";
import { CalendarStyled, HeaderStyled } from "./Header.styled";
import { ICON } from "../../../constants/icons";

const months = Object.values(MONTH);

const Header = ({
  date,
  monthDate,
  decreaseMonth,
  increaseMonth,
  changeMonth,
  changeYear,
  nextMonthButtonDisabled,
  prevMonthButtonDisabled
}: ReactDatePickerCustomHeaderProps) => {
  const { lang } = useAppSelector((state) => state.system);
  const localLang = lang.toLocaleLowerCase();
  const { translate } = useLang();
  return (
    <HeaderStyled>
      <div className="control">
        <div className="month-year-menu">
          <ContextMenu
            data={months.map((month, index) => ({
              label: translate(`months.${month.toLocaleLowerCase()}`),
              custom: {
                onClick: () => {
                  changeMonth(index);
                }
              }
            }))}
            trigger="press"
          >
            <div className="month-year">
              {capitalizeFirstLetter(
                moment(monthDate || new Date())
                  .locale(localLang)
                  .format(FORMAT.MONTH_DATE_YEAR_1)
                  .substring(0, 3)
              )}

              <span className="icon-dropdown">
                <HVIcon
                  name="arrow_chevron_down"
                  scale="small"
                  theme="fill"
                  svgProps={{
                    ...ICON.COLOR_FILL
                  }}
                />
              </span>
            </div>
          </ContextMenu>

          <ContextMenu
            data={Array.from({ length: 6 }, (_, i) => {
              const year = moment().year() - (5 - i);
              return {
                label: year.toString(),
                custom: {
                  onClick: () => {
                    changeYear(year);
                  }
                }
              };
            })}
            trigger="press"
          >
            <div className="month-year">
              {moment(monthDate || date)
                .locale(localLang)
                .format(FORMAT.DATE_MONTH)
                .substring(0, 4)}
              <span className="icon-dropdown">
                <HVIcon
                  name="arrow_chevron_down"
                  scale="small"
                  theme="fill"
                  svgProps={{
                    ...ICON.COLOR_FILL
                  }}
                />
              </span>
            </div>
          </ContextMenu>
        </div>

        <div className="month-control">
          <HVButton
            variant="Ghost"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!prevMonthButtonDisabled) {
                decreaseMonth();
              }
            }}
            id="prev-month-button"
            buttonContainerProps={{
              h: 32,
              w: 32,
              p: 0
            }}
          >
            <HVIcon name="arrow_chevron_up" svgProps={{ ...ICON.SIZE_20 }} />
          </HVButton>
          <HVButton
            variant="Ghost"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!nextMonthButtonDisabled) {
                increaseMonth();
              }
            }}
            id="next-month-button"
            buttonContainerProps={{
              h: 32,
              w: 32,
              p: 0
            }}
          >
            <HVIcon name="arrow_chevron_down" svgProps={{ ...ICON.SIZE_20 }} />
          </HVButton>
        </div>
      </div>
    </HeaderStyled>
  );
};

export const Calendar = ({ today, ...props }: CalendarProps) => {
  const uniq = useId();
  const { translate } = useLang();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const renderWeekDay = useCallback(
    (nameOfDay: string) =>
      String(translate(`days.${nameOfDay.toLocaleLowerCase()}`)).substring(
        0,
        3
      ),
    []
  );

  const renderDayContents = useCallback(
    (day: number, date: Date | undefined) => {
      return (
        <div
          className={classNames({
            "date-picker-custom-today": moment(date).diff(today, "day") === 0,
            "date-picker-custom-date": true,
            "date-picker-start-of-month":
              moment(date).startOf("month").diff(date, "day") === 0,
            "date-picker-end-of-month":
              moment(date).endOf("month").diff(date, "day") === 0
          })}
        >
          <span data-day={day}>{date?.getDate()}</span>
          <span className="shadow-date">{date?.getDate()}</span>
        </div>
      );
    },
    [today]
  );

  useEffect(() => {
    if (!wrapperRef.current) return undefined;

    const monthContainer = wrapperRef.current.querySelector(
      ".react-datepicker__month"
    ) as HTMLElement;

    const prevButton = wrapperRef.current.querySelector(
      ".month-control #prev-month-button"
    ) as HTMLSpanElement;

    const nextButton = wrapperRef.current.querySelector(
      ".month-control #next-month-button"
    ) as HTMLSpanElement;

    const handleMouseWheelEvent = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.deltaY > 0) {
        nextButton?.click();
      } else {
        prevButton?.click();
      }
    };

    monthContainer?.addEventListener("wheel", handleMouseWheelEvent);

    return () =>
      monthContainer?.removeEventListener("wheel", handleMouseWheelEvent);
  }, [wrapperRef.current]);

  return (
    <CalendarStyled ref={wrapperRef} id={`date-picker_${uniq}`}>
      <DatePicker
        formatWeekDay={renderWeekDay}
        renderDayContents={renderDayContents}
        renderCustomHeader={(props) => <Header {...props} />}
        {...props}
      />
    </CalendarStyled>
  );
};
